import { DateTime } from "luxon";
import {
  AxisValueFormatterContext,
  XAxis,
  ZoomOptions,
} from "@mui/x-charts-premium";
import { TimeWheel } from "shared-ts-utils-luxon";
import { TFunction } from "i18next";
import { AxisGroup } from "@mui/x-charts/internals";

export type TimeBandXAxisTimeUnit =
  | "year"
  | "quarter"
  | "month"
  | "week"
  | "day";

export type MonthFormat = "M" | "MM" | "MMM" | "MMMM" | "MMMMM";

export type GroupingOptions = {
  enabled?: boolean;
  monthFormat?: MonthFormat;
};

export type TimeBandXAxisOptions = {
  id?: string;
  hideTickLabels?: boolean;
  position?: "top" | "bottom" | "none";
  grouping?: GroupingOptions;
  zoom?: ZoomOptions;
  tickInterval?: "auto" | ((value: any, index: number) => boolean) | any[];
};
export class TimeBandXAxis {
  static resolveData(
    timeUnit: TimeBandXAxisTimeUnit,
    startDate: DateTime<true>,
    endDate: DateTime<true>
  ): Array<DateTime<true>> {
    return new TimeWheel({
      start: startDate,
      timeUnit: timeUnit,
    }).runUntilTime(endDate, (dateTime) => {
      return dateTime;
    });
  }

  static resolve(
    timeUnit: TimeBandXAxisTimeUnit,
    startDate: DateTime<true>,
    endDate: DateTime<true>,
    t: TFunction<"shared-ts-mui">,
    options?: TimeBandXAxisOptions
  ): XAxis {
    const data = this.resolveData(timeUnit, startDate, endDate);
    const groupingEnabled = options?.grouping?.enabled === true;
    const hideTickLabels = options?.hideTickLabels === true;
    return groupingEnabled
      ? ({
          id: options?.id ?? "timeBand",
          scaleType: "band",
          data: data,
          tickLabelStyle: hideTickLabels ? { display: "none" } : undefined,
          tickSize: hideTickLabels ? 0 : undefined,
          tickInterval: options?.tickInterval,
          tickLabelPlacement: "middle",
          tickPlacement: "middle",
          height: this.heightProvider(hideTickLabels, timeUnit, options?.zoom),
          groups: this.groupsProvider(
            hideTickLabels,
            timeUnit,
            options?.grouping
          ),
          valueFormatter: this.valueFormatterProvider(timeUnit),
          position: options?.position,
          zoom: options?.zoom,
        } as XAxis)
      : ({
          id: options?.id ?? "timeBand",
          dataKey: "time",
          scaleType: "band",
          label: t("Time"),
          data: data,
          tickLabelStyle: hideTickLabels ? { display: "none" } : undefined,
          tickSize: hideTickLabels ? 0 : undefined,
          tickInterval: options?.tickInterval,
          tickLabelPlacement: "middle",
          tickPlacement: "middle",
          height: this.heightProvider(hideTickLabels, timeUnit, options?.zoom),
          valueFormatter: this.valueFormatterProvider(timeUnit),
          position: options?.position,
          zoom: options?.zoom,
        } as XAxis);
  }

  public static valueFormatterProvider =
    (timeUnit: TimeBandXAxisTimeUnit) =>
    (value: DateTime<true>, context: AxisValueFormatterContext<"band">) => {
      if (timeUnit === "year") {
        return value.toFormat("yyyy").capitalizeFirstLetter();
      } else if (timeUnit === "quarter") {
        return value.toFormat("q yy").capitalizeFirstLetter();
      } else if (timeUnit === "month") {
        return context.location === "tooltip"
          ? value.toFormat("MMMM yy").capitalizeFirstLetter()
          : value.toFormat("MMM yy").capitalizeFirstLetter();
      } else if (timeUnit === "week") {
        return value.toFormat("W yy").capitalizeFirstLetter();
      } else if (timeUnit === "day") {
        return value
          .toLocaleString(DateTime.DATE_SHORT)
          .capitalizeFirstLetter();
      } else {
        return value.year.toString();
      }
    };

  public static heightProvider = (
    hideTickLabels: boolean,
    timeUnit: TimeBandXAxisTimeUnit,
    zoomOptions: TimeBandXAxisOptions["zoom"]
  ) => {
    if (hideTickLabels) {
      return undefined;
    }

    if (timeUnit === "day") {
      return 52 + (zoomOptions?.slider?.enabled === true ? 24 : 0);
    }
    return 30 + (zoomOptions?.slider?.enabled === true ? 24 : 0);
  };

  public static groupsProvider = (
    hideTickLabels: boolean,
    timeUnit: TimeBandXAxisTimeUnit,
    options: GroupingOptions | undefined
  ): AxisGroup[] | undefined => {
    if (hideTickLabels) {
      return undefined;
    }
    if (timeUnit === "day") {
      return [
        {
          getValue: TimeBandXAxis.getDayOfMonth,
          tickSize: 8,
        },
        {
          getValue: TimeBandXAxis.getMonthProvider(options?.monthFormat),
          tickSize: 30,
        },
        {
          getValue: TimeBandXAxis.getYear,
          tickSize: 52,
        },
      ];
    } else if (timeUnit === "month") {
      return [
        {
          getValue: TimeBandXAxis.getMonthProvider(options?.monthFormat),
          tickSize: 8,
        },
        {
          getValue: TimeBandXAxis.getYear,
          tickSize: 30,
        },
      ];
    }
    return undefined;
  };

  private static getDayOfMonth = (date: DateTime): string => {
    return date.toFormat("dd");
  };

  private static getMonthProvider =
    (format: MonthFormat = "MMMM") =>
    (date: DateTime): string => {
      return date.toFormat(format).capitalizeFirstLetter();
    };

  private static getYear = (date: DateTime) => {
    return date.toFormat("yyyy");
  };
}
